import { buildCollectFromExecutionPath } from './collectPath';
import { AgentExecutionNotFoundError, mapAgentLogsApiError } from './errors';
import {
  buildAgentExecutionDetailFromExecutionLogs,
  toAgentExecutionListItems
} from './transforms';
import type {
  AgentExecutionsListResponse,
  AgentExecutionDetail,
  CollectExecutionToDatasetParams,
  CollectExecutionToDatasetResponse,
  GetAgentExecutionDetailParams,
  ListAgentExecutionsParams,
  PipelineLogsAgentItem,
  PipelineLogsAgentResponse
} from './types';

import {
  buildPipelineLogsRequest,
  MONITOR_PAGE_SIZE,
  PIPELINE_LOGS_PATH
} from '~/api/monitorService';
import test from '~/api/testV2';
import axiosInstance from '~/common/helpers/axiosInstance';

type FetchAgentPipelineLogsParams = Parameters<
  typeof buildPipelineLogsRequest
>[0];

async function fetchAgentPipelineLogs(
  params: FetchAgentPipelineLogsParams
): Promise<PipelineLogsAgentResponse> {
  const request = buildPipelineLogsRequest(params);

  const response = await axiosInstance.post<PipelineLogsAgentResponse>(
    PIPELINE_LOGS_PATH,
    request,
    { params: { environment: params.environment } }
  );

  return response.data;
}

export async function listAgentExecutions({
  realm,
  environment,
  filters,
  dateFrom,
  dateTo,
  limit = MONITOR_PAGE_SIZE,
  order = 'descending',
  cursor
}: ListAgentExecutionsParams): Promise<AgentExecutionsListResponse> {
  try {
    const response = await fetchAgentPipelineLogs({
      realm,
      environment,
      filters: { ...filters, logType: 'AGENT' },
      dateFrom,
      dateTo,
      limit,
      order,
      cursor
    });

    const items = toAgentExecutionListItems(
      response.data as PipelineLogsAgentItem[]
    );

    return {
      items,
      pagination: response.pagination,
      logTotal: response.total,
      isEmpty: items.length === 0
    };
  } catch (error) {
    throw mapAgentLogsApiError(error);
  }
}

export async function getAgentExecutionDetail({
  realm,
  environment,
  agentExecutionKey,
  connectorId,
  pipelineName,
  pipelineVersion,
  pipelineKey,
  agentName
}: GetAgentExecutionDetailParams): Promise<AgentExecutionDetail> {
  try {
    const result = await test.getLogs({
      realm,
      key: pipelineKey,
      env: environment
    });

    const detail = buildAgentExecutionDetailFromExecutionLogs(
      agentExecutionKey,
      { pipelineKey, pipelineName, pipelineVersion, connectorId, agentName },
      result?.logs ?? []
    );

    if (!detail) {
      throw new AgentExecutionNotFoundError(agentExecutionKey);
    }

    return detail;
  } catch (error) {
    throw mapAgentLogsApiError(error);
  }
}

export async function collectExecutionToDataset({
  realm,
  environment,
  pipelineName,
  pipelineVersion,
  connectorId,
  datasetId,
  agentExecutionKey,
  pipelineKey,
  payload
}: CollectExecutionToDatasetParams): Promise<CollectExecutionToDatasetResponse> {
  const path = buildCollectFromExecutionPath(
    realm,
    pipelineName,
    pipelineVersion,
    connectorId,
    datasetId
  );

  try {
    const response =
      await axiosInstance.post<CollectExecutionToDatasetResponse>(path, {
        environment,
        agentExecutionKey,
        pipelineKey,
        testCase: {
          name: payload.name,
          variables: payload.variables,
          ...(payload.expectedResults && {
            expectedResults: payload.expectedResults
          }),
          ...(payload.entityVersion !== undefined && {
            entityVersion: payload.entityVersion
          })
        }
      });

    return response.data;
  } catch (error) {
    throw mapAgentLogsApiError(error);
  }
}

export const agentLogsQueryKeys = {
  all: ['agent-logs'] as const,
  lists: () => [...agentLogsQueryKeys.all, 'list'] as const,
  list: (
    params: Omit<ListAgentExecutionsParams, 'cursor'> & { cursor?: string }
  ) => [...agentLogsQueryKeys.lists(), params] as const,
  details: () => [...agentLogsQueryKeys.all, 'detail'] as const,
  detail: (params: GetAgentExecutionDetailParams) =>
    [...agentLogsQueryKeys.details(), params] as const,
  detailByExecutionKey: (params: {
    realm: string;
    environment: string;
    agentExecutionKey: string;
  }) => [...agentLogsQueryKeys.details(), 'by-key', params] as const,
  collect: () => [...agentLogsQueryKeys.all, 'collect'] as const
};
